/**
 * The route exports of the two write-back sub-pages (client rule 3, package A 2026-09-30):
 * `/inseason/write-back/pack` and `/inseason/write-back/backlog`. Each is the file the page's own
 * table downloads (`packLinesRequest`), read from the run in the URL; with no run named, a file
 * with no line — the run is never guessed (CLAUDE.md §3.4).
 *
 * A group opened on the page (`?view=groups&by=&group=`, DEC-105, ADR 9042) narrows the file to the
 * group's lines: by the read's own filter where it takes one, by the line's kind or run otherwise.
 * A store group the pinned reads cannot filter leaves as no line rather than as the whole pack.
 */

import { callOperation } from '@/api';
import { executionPack, marketExecutionPack } from '@/api/generated';
import type { ExportRequest } from '@/export';

import type { ExportScope } from '../manifest';
import { packLines, packLinesRequest, type PackLine } from './execution-pack';
import { readScope } from './scope';
import { groupLinesQuery, lineInGroup, readWriteBackGroupScope, readWriteBackView, type WriteBackQueue } from './write-back-groups';

function searchOf(given?: ExportScope): string {
  return given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
}

function runOf(given?: ExportScope): string | null {
  return readScope(searchOf(given)).runId;
}

/** The group the link opens, when it opens one; `null` for the whole page. */
function groupOf(queue: WriteBackQueue, given?: ExportScope) {
  const search = searchOf(given);
  // An explicit `?view=groups` only: the export does not know whether the read is pinned.
  if (readWriteBackView(search, false) !== 'groups') return null;
  const scope = readWriteBackGroupScope(search, queue);
  return scope.group === null ? null : { by: scope.by, key: scope.group };
}

/** The lines of the opened group, or every line. */
function narrow(lines: readonly PackLine[], group: ReturnType<typeof groupOf>): readonly PackLine[] {
  if (group === null) return lines;
  return lines.filter((line) => lineInGroup(line, group.by, group.key) === true);
}

/** The run's execution pack, line for line — the pack page's table. */
export async function executionPackExport(given?: ExportScope): Promise<ExportRequest> {
  const runId = runOf(given);
  if (runId === null) return packLinesRequest({ country_key: '', state: null }, []);
  const group = groupOf('pack', given);
  const extra = group === null ? {} : groupLinesQuery(group.by, group.key);
  const pack = await callOperation(executionPack, { path: { run_id: runId }, query: extra as Record<string, never> });
  return packLinesRequest(pack, narrow(packLines(pack), group));
}

/** What the run's market still owes, from every run — the backlog page's table. */
export async function backlogExport(given?: ExportScope): Promise<ExportRequest> {
  const runId = runOf(given);
  if (runId === null) return packLinesRequest({ country_key: '', state: 'open' }, []);
  const run = await callOperation(executionPack, { path: { run_id: runId }, query: {} });
  const group = groupOf('backlog', given);
  const extra = group === null ? {} : groupLinesQuery(group.by, group.key);
  const pack = await callOperation(marketExecutionPack, { query: { country_key: run.country_key, state: 'open', ...extra } });
  return packLinesRequest(pack, narrow(packLines(pack), group));
}
